import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Search, 
  ChevronDown, 
  ChevronUp, 
  MessageCircle, 
  Mail, 
  Phone, 
  BookOpen,
  HelpCircle,
  Settings,
  CreditCard,
  Smartphone,
  Globe,
  Shield,
  FileText,
  Users,
  BarChart3,
  ExternalLink
} from 'lucide-react';
import LandingPageLayout from '../components/LandingPageLayout';
import LanguageSelector from '../components/LanguageSelector';

const HelpCenterPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  // 페이지 로드 시 상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const categories = [
    {
      icon: <BookOpen className="h-6 w-6" />,
      title: t('help.categories.gettingStarted.title', 'Getting Started'),
      description: t('help.categories.gettingStarted.description', 'Learn the basics of using ScanVCF'),
      articles: [
        {
          question: t('help.gettingStarted.q1.question', 'What devices and OS versions are supported?'),
          answer: t('help.gettingStarted.q1.answer', 'ScanVCF supports iOS 15+ and Android 8.0+. For the best experience, we recommend using the latest versions of your operating system.')
        },
        {
          question: t('help.gettingStarted.q2.question', 'What are the recommended image specifications?'),
          answer: t('help.gettingStarted.q2.answer', 'Profile images: 512×512px, Logo images: 512×512px, Banner images: 1200×628px. Supported formats: JPG, PNG, WEBP. Maximum file size: 5MB per image.')
        },
        {
          question: t('help.gettingStarted.q3.question', 'How do vCard fields map to contact information?'),
          answer: t('help.gettingStarted.q3.answer', 'Name maps to contact name, Title to job title, Company to organization, Mobile to phone number, Email to email address, Address to physical address. Use E.164 format for phone numbers (e.g., +61 4XX XXX XXX).')
        },
        {
          question: t('help.gettingStarted.q4.question', 'How do public/private settings and URLs work?'),
          answer: t('help.gettingStarted.q4.answer', 'Public cards are accessible at scanvcf.com/username. When you change your username, existing QR codes and URLs will need to be regenerated and reprinted.')
        },
        {
          question: t('help.gettingStarted.q5.question', 'How do I create my first business card?'),
          answer: t('help.gettingStarted.q5.answer', 'Click "Create Card" in the dashboard, fill in your information, upload your profile image and logo, then save. You can customize the design and add social media links.')
        }
      ]
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: t('help.categories.sharingSaving.title', 'Sharing & Saving'),
      description: t('help.categories.sharingSaving.description', 'How to save contacts and share your cards'),
      articles: [
        {
          question: t('help.sharingSaving.q1.question', 'How do I save contacts on iPhone?'),
          answer: t('help.sharingSaving.q1.answer', 'After downloading the .vcf file, tap "Add to Contacts" when prompted. If popup is blocked, save to Files app first, then open the .vcf file and tap Share > Add to Contacts. Use Contacts app merge feature for duplicates.')
        },
        {
          question: t('help.sharingSaving.q2.question', 'How do I save contacts on Android?'),
          answer: t('help.sharingSaving.q2.answer', 'Tap the .vcf file and select "Contacts" app to open. If default app is set incorrectly, reset default app associations in Settings. Update Google Contacts app for best compatibility.')
        },
        {
          question: t('help.sharingSaving.q3.question', 'What are the QR code printing guidelines?'),
          answer: t('help.sharingSaving.q3.answer', 'Minimum size: 2.5cm × 2.5cm. Ensure high contrast (dark code on light background), adequate margins, avoid glossy coatings, and test scan before mass printing. Avoid distortion and damage.')
        }
      ]
    },
    {
      icon: <Settings className="h-6 w-6" />,
      title: t('help.categories.account.title', 'Account & Settings'),
      description: t('help.categories.account.description', 'Manage your account and preferences'),
      articles: [
        {
          question: t('help.account.q1.question', 'How do I change my username (handle)?'),
          answer: t('help.account.q1.answer', 'Go to Settings > Profile > Username. Note: Changing your username will require regenerating and reprinting any QR codes or links you\'ve already shared.')
        },
        {
          question: t('help.account.q2.question', 'How do I export my data or delete my account?'),
          answer: t('help.account.q2.answer', 'Export: Settings > Account > Export Data (CSV/VCF formats). Delete: Settings > Account > Delete Account. This action cannot be undone and is separate from subscription cancellation.')
        },
        {
          question: t('help.account.q3.question', 'How do I update my profile information?'),
          answer: t('help.account.q3.answer', 'Navigate to Settings > Profile to update your personal information, contact details, and preferences.')
        }
      ]
    },
    {
      icon: <CreditCard className="h-6 w-6" />,
      title: t('help.categories.billing.title', 'Billing & Subscriptions'),
      description: t('help.categories.billing.description', 'Manage your subscription and payments'),
      articles: [
        {
          question: t('help.billing.q1.question', 'How do I restore my subscription?'),
          answer: t('help.billing.q1.answer', 'iOS: App Store > Account > Purchases > Restore Purchases. Android: Play Store > Account > Subscriptions > Restore. Web: Check billing history in dashboard. Note: Subscriptions cannot be transferred between App Store, Google Play, and Web platforms.')
        },
        {
          question: t('help.billing.q2.question', 'How do upgrades and downgrades work?'),
          answer: t('help.billing.q2.answer', 'Upgrades take effect immediately with prorated billing. Downgrades take effect at the next billing cycle. Check your billing settings for specific timing.')
        },
        {
          question: t('help.billing.q3.question', 'How do I get receipts and handle refunds?'),
          answer: t('help.billing.q3.answer', 'Receipts are sent to your email and available in your account billing section. All payments are processed in USD. Refunds follow store policies (App Store/Google Play) or our refund policy for web payments.')
        },
        {
          question: t('help.billing.q4.question', 'How do I cancel my subscription?'),
          answer: t('help.billing.q4.answer', 'You can cancel your subscription at any time by going to Settings > Billing > Cancel Subscription. Your access will continue until the end of your current billing period.')
        }
      ]
    },
    {
      icon: <Smartphone className="h-6 w-6" />,
      title: t('help.categories.mobileApp.title', 'Mobile App'),
      description: t('help.categories.mobileApp.description', 'Download and sync with mobile devices'),
      articles: [
        {
          question: t('help.mobileApp.q1.question', 'How do I download and set up the mobile app?'),
          answer: t('help.mobileApp.q1.answer', 'Download from Google Play Store (Android) or App Store (iOS). Sign in with the same account as your web dashboard. Your data will automatically sync across all devices.')
        },
        {
          question: t('help.mobileApp.q2.question', 'How does data synchronization work?'),
          answer: t('help.mobileApp.q2.answer', 'Data syncs automatically when connected to internet. For manual refresh, pull down on the dashboard or use the refresh button. Changes are synced in real-time across all devices.')
        },
        {
          question: t('help.mobileApp.q3.question', 'Why does the app need camera and storage permissions?'),
          answer: t('help.mobileApp.q3.answer', 'Camera: For QR code scanning. Storage/Photos: For uploading profile images and logos. Notifications: Optional for updates and reminders. You can deny permissions and still use basic features.')
        }
      ]
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: t('help.categories.features.title', 'Features & Tools'),
      description: t('help.categories.features.description', 'Learn about advanced features'),
      articles: [
        {
          question: t('help.features.q1.question', 'How do I customize QR code design?'),
          answer: t('help.features.q1.answer', 'Go to QR Code section > Customize Design. Choose colors with high contrast (dark code on light background), add your logo (avoid complex designs), and test scan before finalizing.')
        },
        {
          question: t('help.features.q2.question', 'How do social media links work?'),
          answer: t('help.features.q2.answer', 'Add social media profiles in the Links section. The system automatically adds https:// prefix if missing. You can customize display order and add multiple platforms.')
        },
        {
          question: t('help.features.q3.question', 'What do analytics metrics mean?'),
          answer: t('help.features.q3.answer', 'Views: Card page visits. Scans: QR code scans. Saves: Contact downloads. Use UTM parameters in links for detailed tracking. Analytics update in real-time.')
        },
        {
          question: t('help.features.q4.question', 'How do I customize my business card design?'),
          answer: t('help.features.q4.answer', 'Go to your business card editor and use the Design tab to change colors, fonts, layouts, and add your logo. You can preview changes in real-time.')
        }
      ]
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: t('help.categories.troubleshooting.title', 'Troubleshooting'),
      description: t('help.categories.troubleshooting.description', 'Common issues and solutions'),
      articles: [
        {
          question: t('help.troubleshooting.q1.question', 'iPhone: .vcf file won\'t open or add to contacts'),
          answer: t('help.troubleshooting.q1.answer', 'Enable popups in Safari settings. Save .vcf to Files app first, then open and tap Share > Add to Contacts. For iOS 17+, allow the security prompt. Use Contacts app merge feature for duplicates.')
        },
        {
          question: t('help.troubleshooting.q2.question', 'Android: "Cannot open file" error'),
          answer: t('help.troubleshooting.q2.answer', 'Update Google Contacts app. Reset default app associations in Settings > Apps > Default Apps. Open .vcf with Contacts app specifically.')
        },
        {
          question: t('help.troubleshooting.q3.question', 'QR code scans but profile won\'t load'),
          answer: t('help.troubleshooting.q3.answer', 'Check internet connection (WiFi/mobile data). Disable VPN if active. Corporate networks may block external sites. Try from different network.')
        },
        {
          question: t('help.troubleshooting.q4.question', 'Web dashboard shows "Profile not found" after refresh'),
          answer: t('help.troubleshooting.q4.answer', 'Clear browser cache and cookies. This is a SPA routing issue - try refreshing the page or navigating back to dashboard from the main menu.')
        },
        {
          question: t('help.troubleshooting.q5.question', 'My QR code is not working'),
          answer: t('help.troubleshooting.q5.answer', 'Ensure your business card is published and the QR code is not damaged. Try regenerating the QR code or check if your card URL is accessible.')
        }
      ]
    }
  ];

  const filteredCategories = categories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  return (
    <LandingPageLayout currentPage="help">
      <div className="min-h-screen bg-gray-50 pt-32 md:pt-0">
        {/* Hero Section */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {t('help.hero.title', 'Help Center')}
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                {t('help.hero.subtitle', 'Find answers to common questions and learn how to get the most out of ScanVCF')}
              </p>
              
              {/* Language Selector */}
              <div className="mb-6">
                <LanguageSelector variant="button" />
              </div>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('help.search.placeholder', 'Search for help articles...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Contact */}
        <div className="lux-dark-section rounded-2xl border border-[#c8a45e]/25 p-6 mb-12 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="text-center lg:text-left">
              <h2 className="text-2xl font-bold tracking-tight mb-2">
                {t('help.contact.title', 'Still need help?')}
              </h2>
              <p className="text-white/60 mb-2">
                {t('help.contact.subtitle', 'Our support team is here to help you')}
              </p>
              <p className="text-white/40 text-sm">
                {t('help.contact.responseTime', 'Response time: AEST 10:00–17:00 (Mon-Fri)')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
              <Link
                to="/contact"
                className="flex items-center justify-center px-4 py-3 bg-white text-primary-600 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                <Mail className="h-4 w-4 mr-2" />
                {t('help.contact.email', 'Email Support')}
              </Link>
              <button
                onClick={() => {
                  // mailto 링크 시도
                  const mailtoLink = 'mailto:support@scanvcf.com';
                  const link = document.createElement('a');
                  link.href = mailtoLink;
                  link.click();
                  
                  // 백업: 클립보드에 복사
                  setTimeout(() => {
                    navigator.clipboard.writeText('support@scanvcf.com').then(() => {
                      alert('이메일 주소가 클립보드에 복사되었습니다: support@scanvcf.com');
                    }).catch(() => {
                      // 클립보드 복사 실패 시 대체 방법
                      const textArea = document.createElement('textarea');
                      textArea.value = 'support@scanvcf.com';
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      alert('이메일 주소가 클립보드에 복사되었습니다: support@scanvcf.com');
                    });
                  }, 100);
                }}
                className="flex items-center justify-center px-4 py-3 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors font-medium"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('help.contact.direct', 'Contact Directly')}
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {filteredCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="text-primary-600 mr-4">
                    {category.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {category.title}
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {category.description}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="divide-y divide-gray-200">
                {category.articles.map((article, articleIndex) => {
                  const globalIndex = categoryIndex * 100 + articleIndex;
                  const isExpanded = expandedItems.includes(globalIndex);
                  
                  return (
                    <div key={articleIndex} className="p-6">
                      <button
                        onClick={() => toggleExpanded(globalIndex)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                          {article.question}
                        </h3>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      
                      {isExpanded && (
                        <div className="mt-4 text-gray-600 leading-relaxed">
                          {article.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {searchQuery && filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('help.noResults.title', 'No results found')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('help.noResults.description', 'Try searching with different keywords or contact our support team')}
            </p>
            <Link
              to="/contact"
              className="lux-btn-gold inline-flex items-center px-5 py-2.5 rounded-full font-semibold"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {t('help.noResults.contact', 'Contact Support')}
            </Link>
          </div>
        )}
      </div>
      </div>
    </LandingPageLayout>
  );
};

export default HelpCenterPage;